import type { Bone, PreferredAngles } from '../lib/skeleton/core';
import { getJointOffsetRotationForBoneRef } from '../lib/io/jointOffsets';

export const BONE_PROPERTIES_BADGE_SIZE_PX = 14;
export const BONE_PROPERTIES_BADGE_GAP_PX = 4;

export type BonePropertyIndicatorState = {
  limits: boolean;
  angleBias: boolean;
  postOffset: boolean;
};

export const EMPTY_BONE_PROPERTY_INDICATOR_STATE: BonePropertyIndicatorState = {
  limits: false,
  angleBias: false,
  postOffset: false,
};

/** True if preferred angles are enabled with at least one non-zero axis. */
export function preferredAnglesAreSignificant(
  pa: PreferredAngles | null | undefined,
): boolean {
  if (pa == null) return false;
  return pa.x !== 0 || pa.y !== 0 || pa.z !== 0;
}

function jointOffsetIsSignificant(
  ioExtras: Record<string, unknown>,
  boneRef: string,
): boolean {
  const t = getJointOffsetRotationForBoneRef(ioExtras, boneRef);
  if (!t) return false;
  return t[0] !== 0 || t[1] !== 0 || t[2] !== 0;
}

export function getBonePropertyIndicatorState(
  bone: Pick<Bone, 'properties' | 'name'> &
    Partial<Pick<Bone, 'internalRef'>>,
  ioExtras: Record<string, unknown> = {},
): BonePropertyIndicatorState {
  const boneRef = bone.internalRef ?? bone.name;
  return {
    limits: bone.properties?.rotationLimits?.hasAny ?? false,
    angleBias: preferredAnglesAreSignificant(bone.properties?.preferredAngles),
    postOffset: jointOffsetIsSignificant(ioExtras, boneRef),
  };
}

export function getBonePropertyTypeLabels(
  state: BonePropertyIndicatorState,
): string[] {
  const labels: string[] = [];
  if (state.limits) labels.push('Limits');
  if (state.angleBias) labels.push('Angle Bias');
  if (state.postOffset) labels.push('Post Offset');
  return labels;
}

/**
 * Whether the outliner / viewer should show the "properties" indicator for this bone.
 * Uses mapping ioExtras for joint_offsets, and only counts bone properties that are
 * semantically non-default (rotation limits, non-zero preferred angles).
 */
export function hasBoneProperties(
  bone: Pick<Bone, 'properties' | 'name'> &
    Partial<Pick<Bone, 'internalRef'>>,
  ioExtras: Record<string, unknown> = {},
): boolean {
  const state = getBonePropertyIndicatorState(bone, ioExtras);
  return state.limits || state.angleBias || state.postOffset;
}
